import { useMutation, useQuery, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { MetaApiResponse } from 'utils/types/CommonApiResponse';
import { Location } from './types';
import { createLocation, deleteLocation, getLocation, getLocations, updateLocation } from './api';

export function useGetLocations(
  params?: any,
  options?: UseQueryOptions<{ data: Location[]; meta: MetaApiResponse }, Error>
) {
  return useQuery<{ data: Location[]; meta: MetaApiResponse }>({
    queryKey: ['locations', params],
    queryFn: () => getLocations(params),
    ...options
  });
}

export function useGetLocation(params?: Record<string, any>, options?: UseQueryOptions<Location, Error>) {
  const { locationId } = useParams<{ locationId: string }>();

  return useQuery<Location>({
    queryKey: ['location', locationId, params],
    queryFn: () => getLocation(locationId!, params),
    enabled: Boolean(locationId),
    ...options
  });
}

export function useCreateOrUpdateLocation() {
  const navigate = useNavigate();
  const { locationId } = useParams<{ locationId: string }>();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Location) => (locationId ? updateLocation(locationId!, payload) : createLocation(payload)),
    onSuccess: async () => {
      await queryClient.refetchQueries({
        queryKey: ['locations']
      });
      await queryClient.refetchQueries({
        queryKey: ['location']
      });
      navigate(-1);
    }
  });
}

export function useDeleteLocation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteLocation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['locations'],
        refetchType: 'active'
      });
    }
  });
}
